import React from 'react'

import { formatDistanceToNow } from 'date-fns'
import { X } from 'lucide-react'
import { action, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'

import { ITileset, TilesetProcessingStatus } from '~/client/graph'
import { Button } from '~/client/src/shared/components/Button/Button'
import { Progress } from '~/client/src/shared/components/shadcn-ui/progress'
import Localization from '~/client/src/shared/localization/LocalizationManager'

interface IProps {
  tileset: ITileset
  onHide?: () => void
}

export const LOCAL_STORAGE_TILESET_NOTIFICATION_PREFIX =
  'hidden-tileset-notification-'

// A processing/queued tileset should only look *actively* working (animated
// progress bar) while it keeps making progress. If its last activity was long
// ago, the backend job is stalled/dead — show text only instead of a bar that
// animates forever.
const ACTIVE_PROCESSING_WINDOW_MS = 15 * 60 * 1000 // 15 minutes

const STATUS_DOT_CLASS: Partial<Record<TilesetProcessingStatus, string>> = {
  [TilesetProcessingStatus.Processing]: 'tw:bg-primary',
  [TilesetProcessingStatus.Queued]: 'tw:bg-primary',
  [TilesetProcessingStatus.Success]: 'tw:bg-success',
  [TilesetProcessingStatus.Failed]: 'tw:bg-destructive',
  [TilesetProcessingStatus.Superseded]: 'tw:bg-muted-foreground',
}

@observer
export default class TilesetNotificationCard extends React.Component<IProps> {
  // Observable "current time" read by isActivelyProcessing. Date.now() is not
  // reactive, so on its own the animated bar would never drop when the freshness
  // window elapses unless some unrelated store update happened to re-render the
  // card. A one-shot timer advances this exactly at the deadline to force it.
  @observable private now = Date.now()
  private staleTimer?: ReturnType<typeof setTimeout>

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  public componentDidMount(): void {
    this.scheduleStaleCheck()
  }

  public componentDidUpdate(): void {
    this.scheduleStaleCheck()
  }

  public componentWillUnmount(): void {
    this.clearStaleTimer()
  }

  @action.bound
  private hideNotification(): void {
    const { tileset, onHide } = this.props
    if (onHide) {
      onHide()
    } else {
      // Fallback for backwards compatibility
      localStorage.setItem(
        LOCAL_STORAGE_TILESET_NOTIFICATION_PREFIX + tileset.id,
        'true',
      )
    }
  }

  @action.bound
  private markNow(): void {
    this.now = Date.now()
  }

  private clearStaleTimer(): void {
    if (this.staleTimer) {
      clearTimeout(this.staleTimer)
      this.staleTimer = undefined
    }
  }

  // Schedule a single re-render for the moment this tileset's freshness window
  // elapses, so isActivelyProcessing flips to false (bar + hint drop) even if
  // the backend never sends another status update.
  private scheduleStaleCheck(): void {
    this.clearStaleTimer()
    const lastActivityAt = this.lastActivityAt
    if (!this.isProcessing || !lastActivityAt) return
    const msUntilStale =
      lastActivityAt + ACTIVE_PROCESSING_WINDOW_MS - Date.now()
    if (msUntilStale <= 0) return
    this.staleTimer = setTimeout(this.markNow, msUntilStale)
  }

  private get lastActivityAt(): number | undefined {
    const { updatedAt, createdAt } = this.props.tileset
    return updatedAt ?? createdAt ?? undefined
  }

  private get isProcessing(): boolean {
    const { status } = this.props.tileset
    return (
      status === TilesetProcessingStatus.Processing ||
      status === TilesetProcessingStatus.Queued
    )
  }

  // Whether the tileset is *really* still working — processing/queued AND
  // recently active. Drives the animated progress bar so a stalled job that is
  // stuck in "processing" doesn't animate indefinitely. Reads the observable
  // `now` so the timer above can force it false when the window elapses.
  private get isActivelyProcessing(): boolean {
    const lastActivityAt = this.lastActivityAt
    if (!this.isProcessing) return false
    // No timestamp to judge by — assume it's a fresh upload and show the bar.
    if (!lastActivityAt) return true
    return this.now - lastActivityAt < ACTIVE_PROCESSING_WINDOW_MS
  }

  private get statusText(): string {
    const { processingTheUpload, uploadSucceeded, uploadFailed, replaced } =
      Localization.translator
    switch (this.props.tileset.status) {
      case TilesetProcessingStatus.Queued:
      case TilesetProcessingStatus.Processing:
        return processingTheUpload
      case TilesetProcessingStatus.Success:
        return uploadSucceeded
      case TilesetProcessingStatus.Failed:
        return uploadFailed
      case TilesetProcessingStatus.Superseded:
        return replaced
      default:
        return ''
    }
  }

  // Secondary hint line, shown only where it helps: a reassurance while the map
  // is actively being processed (appears/disappears with the progress bar), or
  // an explanation for a replaced file.
  private get secondaryText(): string | null {
    if (this.isActivelyProcessing) {
      return Localization.translator.pleaseWaitThisMayTakeAWhile
    }
    if (this.props.tileset.status === TilesetProcessingStatus.Superseded) {
      return Localization.translator.aNewerVersionOfThisFileWasUploaded
    }
    return null
  }

  public render(): React.ReactNode {
    const { tileset } = this.props

    return (
      <div className="tw:m-2.5 tw:rounded-[5px] tw:border tw:border-solid tw:border-border tw:p-[5px]">
        {this.header}
        <div className="tw:break-all tw:px-[5px] tw:text-body-sm tw:font-semibold tw:text-foreground">
          {tileset.name}
        </div>
        {this.isActivelyProcessing && (
          <div className="tw:px-[5px] tw:py-[5px]">
            <Progress value={null} variant="default" />
          </div>
        )}
        {!!tileset?.errors?.length && (
          <div className="tw:m-[5px] tw:rounded-[5px] tw:border tw:border-solid tw:border-destructive tw:p-[5px] tw:text-body-sm tw:text-destructive">
            {tileset.errors[0]}
          </div>
        )}
      </div>
    )
  }

  private get header(): React.ReactNode {
    const { tileset } = this.props
    const dateMsg = formatDistanceToNow(new Date(tileset.createdAt), {
      addSuffix: true,
    })

    return (
      <div className="tw:flex tw:flex-col">
        <div className="tw:flex tw:items-center">
          <div className="tw:flex-1 tw:pl-[5px] tw:text-body-sm tw:text-foreground">
            {dateMsg}
          </div>
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={Localization.translator.dismiss}
            onClick={this.hideNotification.bind(this)}
          >
            <X />
          </Button>
        </div>

        <div className="tw:flex tw:items-center">
          <div
            className={`tw:mx-1.5 tw:size-1.5 tw:grow-0 tw:rounded-full ${
              STATUS_DOT_CLASS[tileset.status] ?? ''
            }`}
          ></div>
          <div className="tw:text-body-sm tw:text-foreground">
            {this.statusText}
          </div>
        </div>
        {this.secondaryText && (
          <div className="tw:pl-[18px] tw:text-body-sm tw:text-muted-foreground">
            {this.secondaryText}
          </div>
        )}
      </div>
    )
  }
}
